import { io, Socket } from 'socket.io-client'
import { EventDispatcher } from '../EventDispatcher'
import { Position } from '../geolocationService/Position'
import { AisLocation } from './AisLocation'

export enum AislocationServiceStatus {
  READY_TO_USE,
  SOCKET_OPEN,
  SOCKET_CLOSED,
  RECONNECTING,
  RECONNECTION_FAILED,
}

export class AislocationService {
  private positionDispatcher: EventDispatcher<Position>
  private statusDispatcher: EventDispatcher<AislocationServiceStatus>
  private status: AislocationServiceStatus =
    AislocationServiceStatus.READY_TO_USE
  private mmsi: string
  private aisUrl: string

  private lastPosition?: Position

  private socket?: Socket

  private predictionInterval?: NodeJS.Timeout
  private reconnectInterval?: NodeJS.Timeout
  private reconnectAttempts = 0
  private readonly RECONNECT_INTERVAL = 5000 // 5 seconds

  constructor(mmsi: string, aisUrl: string) {
    this.positionDispatcher = new EventDispatcher<Position>()
    this.statusDispatcher = new EventDispatcher<AislocationServiceStatus>()
    this.mmsi = mmsi
    this.aisUrl = aisUrl
  }

  private isValidAisLocation(data: unknown): data is AisLocation {
    if (!data || typeof data !== 'object') return false
    
    const candidate = data as any
    
    if (typeof candidate.timestamp !== 'number') return false
    if (!candidate.coords || typeof candidate.coords !== 'object') return false
    if (typeof candidate.coords.latitude !== 'number') return false
    if (typeof candidate.coords.longitude !== 'number') return false
    
    if (candidate.coords.latitude < -90 || candidate.coords.latitude > 90) return false
    if (candidate.coords.longitude < -180 || candidate.coords.longitude > 180) return false
    
    if (candidate.speed !== undefined && candidate.speed !== null) {
      if (typeof candidate.speed !== 'number' || candidate.speed < 0) return false
    }
    
    if (candidate.course !== undefined && candidate.course !== null) {
      if (typeof candidate.course !== 'number' || 
          candidate.course < 0 || 
          candidate.course > 360) return false
    }
    
    return true
  }
  

  private async fetchAISPosition(): Promise<AisLocation> {
    try {
      const response = await fetch(`${this.aisUrl}/api/position/${this.mmsi}`, {
        referrerPolicy: 'unsafe-url',
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch AIS position, http status:${response.status}`)
      }

      const data = (await response.json()) as AisLocation

      if (!this.isValidAisLocation(data)) {
        throw new Error('Invalid AIS position data structure')
      }  

      return data
    } catch (error) {
      console.error('Error fetching AIS position:', error)
      throw error
    }
  }

  private startPredictionInterval(
    position: Position,
    speed: number,
    course: number
  ) {
    this.stopPredictionInterval();
    
    this.schedulePrediction(position, speed, course, 250);
  }
  
  private schedulePrediction(
    position: Position, 
    speed: number, 
    course: number, 
    frequency: number
  ) {
    this.predictionInterval = setTimeout(() => {
      const startTime = Date.now();
      const predictedPosition = position.getLocationPrediction(
        speed,
        course,
        startTime
      );
      const elapsed = Date.now() - startTime;
  
      let newFrequency = frequency;
      if (elapsed > newFrequency) {
        newFrequency += elapsed;
      }
  
      this.triggerNewPosition(predictedPosition);
      
      this.schedulePrediction(predictedPosition, speed, course, newFrequency);
    }, frequency);
  }
  
  private stopPredictionInterval() {
    if (this.predictionInterval) {
      clearTimeout(this.predictionInterval);
      this.predictionInterval = undefined;
    }
  }

  private triggerNewPosition(position: Position) {
    this.positionDispatcher.fire(position)
    this.lastPosition = position
  }

  private handleStatusChange(
    status: AislocationServiceStatus,
    triggerDispatcher = true
  ) {
    this.status = status

    if (triggerDispatcher) {
      this.statusDispatcher.fire(status)
    }
  }

  private setupSocketListeners(firstPosition: AisLocation) {
    if (!this.socket) return;
    
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      if (this.reconnectInterval) {
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = undefined;
      }
      
      this.handleStatusChange(AislocationServiceStatus.SOCKET_OPEN);
      this.triggerNewPosition(Position.fromAislocation(firstPosition));
    });

    this.socket.on('disconnect', (reason) => {
      this.handleStatusChange(AislocationServiceStatus.SOCKET_CLOSED);
      
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        console.log('Socket disconnected:', reason);
      } else {
        this.startReconnectionProcess();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.startReconnectionProcess();
    });

    this.socket.on('position', (positionData: unknown) => {
      if (!this.isValidAisLocation(positionData)) {
        console.error('Received invalid position data:', positionData);
        return;
      }
      
      const position = Position.fromAislocation(positionData);
      
      if (this.predictionInterval) this.stopPredictionInterval();
      
      if (
        positionData.speed &&
        positionData.course !== undefined &&
        positionData.course !== null &&
        positionData.course >= 0 &&
        positionData.course <= 360
      ) {
        this.startPredictionInterval(
          position,
          positionData.speed,
          positionData.course
        );
      }
      
      this.triggerNewPosition(position);
    });
  }

  private startReconnectionProcess() {
    if (this.reconnectInterval) return;
    
    this.handleStatusChange(AislocationServiceStatus.RECONNECTING);
    
    this.reconnectInterval = setInterval(() => {
      this.reconnectAttempts++;
      
      this.socket?.connect();
      
      const backoffTime = Math.min(
        30000, 
        this.RECONNECT_INTERVAL * Math.pow(1.5, this.reconnectAttempts - 1)
      );
      
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = setTimeout(
        () => this.startReconnectionProcess(), 
        backoffTime
      );
    }, this.RECONNECT_INTERVAL);
  }
  

  getStatus() {
    return this.status
  }

  getLastPosition() {
    return this.lastPosition
  }

  async startSocket(): Promise<void> {
    if (this.socket) {
      throw new Error('AislocationService is already running');
    }
  
    if (this.status !== AislocationServiceStatus.READY_TO_USE) {
      throw new Error('AislocationService is not ready to start watching');
    }
  
    try {
      const firstPosition = await this.fetchAISPosition();
      
      this.socket = io(this.aisUrl, {
        query: { mmsi: this.mmsi },
        reconnection: false,
      });
      
      this.setupSocketListeners(firstPosition);
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error starting AIS location service:', error);
      throw error;
    }
  }

  stopSocket() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = undefined;
    }
    
    this.socket?.close();
  }

  // Add method for manual reconnection
  async manualReconnect() {
    if (this.status === AislocationServiceStatus.SOCKET_CLOSED || 
        this.status === AislocationServiceStatus.RECONNECTION_FAILED) {
      this.reconnectAttempts = 0;
      this.handleStatusChange(AislocationServiceStatus.READY_TO_USE);
      await this.startSocket();
    }
  }

  registerPositionListener(listener: (position: Position) => void) {
    this.positionDispatcher.register(listener)
  }

  registerStatusListener(listener: (status: AislocationServiceStatus) => void) {
    this.statusDispatcher.register(listener)
  }

  destroy() {
    this.stopSocket()
    this.positionDispatcher.unregisterAll()
    this.statusDispatcher.unregisterAll()
    if(this.predictionInterval) clearInterval(this.predictionInterval)
    if(this.reconnectInterval) clearInterval(this.reconnectInterval)
  }
}